// FrameView (§23.4): the single per-frame input of render, audio and HUD. DOM- and three-free.
// Frozen after WP0: additive changes only.
import { DEFAULT_COSMETICS, type Cosmetics } from '@shared/cosmetics';
import { MAX_BEANS, NO_SLOT, TICK_HZ, TUNE } from '@shared/constants';
import { BS, EK, ItemKind, MapId, ModeId, MutatorId, RoomPhase } from '@shared/ids';
import { createRoundClock, roundClock, type RoundClock } from '@shared/maps/clock';
import { createHazardPose, type HazardPose, type MapDef } from '@shared/maps/mapTypes';
import { BeanFlag, type SnapBean, type SnapEnt } from '@shared/protocol/schema';
import { createBean, createMapState, deriveBeanStats } from '@shared/sim/world';
import type { Bean, Entity, MapState, RoundConfig, World } from '@shared/types';

export interface BeanView {
  slot: number; active: boolean; isLocal: boolean; isBot: boolean; connected: boolean;
  name: string; identity: number; team: number; cosmetics: Cosmetics; voicePitch: number;
  state: BS; st: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; facing: number;
  radius: number; dizzy: number; item: ItemKind; charges: number;
  out: boolean; ghost: boolean; hasPotato: boolean; holdsCrown: boolean; crowned: boolean;
  big: boolean; floating: boolean; invuln: boolean; partner: number;
  edgeX: number; edgeY: number; edgeNx: number; edgeNy: number;
  place: number; crownTicks: number; readyTicks: number; score: number;
  /**
   * Ticks left of the §2.9 ledge cooldown (`cdLedge`, additive for INT-B's telegraph): while > 0
   * a knock-off over the rim is a fall, not a ledge hang (it starts at 300 when a hang ends).
   */
  ledgeCd: number;
}
export interface EntityView { id: number; kind: EK; owner: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; t: number; timer: number; data: number; data2: number; pending: boolean }

export type ReplayKind = 'instant' | 'bonkcam' | 'potm';
export interface ModeView { potatoSlot: number; potatoIntensity: number; crownHolder: number; crownGoalTicks: number; matchPoint: boolean; timeLeftSec: number; alive: number }
export interface NetView { online: boolean; rtt: number; jitter: number; predErrP95: number }
export interface ReplayView { kind: ReplayKind; t: number; duration: number; focusSlot: number; killerSlot: number }

export interface FrameView {
  now: number; dt: number; timeScale: number;
  source: 'online' | 'local' | 'replay';
  roomPhase: RoomPhase; mapId: MapId; modeId: ModeId; golden: boolean; mutator: MutatorId;
  roundIndex: number; totalRounds: number; roundId: number;
  renderTick: number; localTick: number; roundTick: number;     // fractional
  clock: RoundClock;
  localSlot: number; focusSlot: number;
  beans: BeanView[];                // length 8 (inactive entries have active=false)
  ents: EntityView[];
  map: MapState; hazard: HazardPose;
  mode: ModeView;
  net: NetView;
  replay: null | ReplayView;
  excitement: number;               // 0..1 (game/Excitement.ts)
  grade: 'normal' | 'gold' | 'ghost' | 'game';
}

/** A per-frame view producer (§23.2): `ClientGame`, `LocalSession` and replay sources. */
export interface FrameSource { frame(now: number): FrameView }

/** An inactive bean slot; sources overwrite it in place each frame. */
export function createBeanView(slot: number): BeanView {
  return {
    slot, active: false, isLocal: false, isBot: false, connected: true,
    name: '', identity: slot, team: 0, cosmetics: { ...DEFAULT_COSMETICS }, voicePitch: 1,
    state: BS.NORMAL, st: 0, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, facing: 0,
    radius: 0, dizzy: 0, item: ItemKind.NONE, charges: 0,
    out: false, ghost: false, hasPotato: false, holdsCrown: false, crowned: false,
    big: false, floating: false, invuln: false, partner: NO_SLOT,
    edgeX: 0, edgeY: 0, edgeNx: 0, edgeNy: 0,
    place: 0, crownTicks: 0, readyTicks: 0, score: 0,
    ledgeCd: 0,
  };
}

/** An empty Lobby frame with all 8 bean slots and fixed-length pose arrays allocated. */
export function createFrameView(): FrameView {
  return {
    now: 0, dt: 0, timeScale: 1,
    source: 'local',
    roomPhase: RoomPhase.LOBBY, mapId: MapId.LOBBY, modeId: ModeId.LOBBY, golden: false, mutator: MutatorId.NONE,
    roundIndex: 0, totalRounds: 0, roundId: 0,
    renderTick: 0, localTick: 0, roundTick: 0,
    clock: createRoundClock(),
    localSlot: NO_SLOT, focusSlot: NO_SLOT,
    beans: Array.from({ length: MAX_BEANS }, (_, slot) => createBeanView(slot)),
    ents: [],
    map: createMapState(), hazard: createHazardPose(),
    mode: { potatoSlot: NO_SLOT, potatoIntensity: 0, crownHolder: NO_SLOT, crownGoalTicks: 0, matchPoint: false, timeLeftSec: 0, alive: 0 },
    net: { online: false, rtt: 0, jitter: 0, predErrP95: 0 },
    replay: null,
    excitement: 0,
    grade: 'normal',
  };
}

// ---- Builders (additive): fill views in place from sim state or decoded snapshots --------------
// Every builder overwrites an existing view object so per-frame sources allocate nothing.

/** Per-slot presentation data that the sim does not carry. A `PlayerDTO` satisfies it. */
export interface BeanIdentity {
  name: string; isBot: boolean; connected: boolean; identity: number; team: number;
  cosmetics: Cosmetics; voicePitch: number; score: number;
}

/**
 * Writes the identity fields of `out`; null restores the empty-slot defaults. Cosmetics are copied
 * into the view's own object, so its identity stays stable and sources are never aliased.
 */
export function setBeanIdentity(out: BeanView, p: BeanIdentity | null): void {
  out.name = p ? p.name : '';
  out.isBot = p ? p.isBot : false;
  out.connected = p ? p.connected : true;
  out.identity = p ? p.identity : out.slot;
  out.team = p ? p.team : 0;
  Object.assign(out.cosmetics, p ? p.cosmetics : DEFAULT_COSMETICS);
  out.voicePitch = p ? p.voicePitch : 1;
  out.score = p ? p.score : 0;
}

// Big Shake stats are derived in one place (deriveBeanStats); snapshot records reuse it.
const statBean = createBean(0);

function radiusFor(shakeTicks: number): number {
  statBean.shakeTicks = shakeTicks;
  deriveBeanStats(statBean);
  return statBean.radius;
}

function setDerivedFlags(out: BeanView, shakeTicks: number, floatTicks: number, invulnTicks: number): void {
  out.ghost = out.state === BS.GHOST;
  out.big = shakeTicks > 0;
  out.floating = out.state === BS.FLOAT || floatTicks > 0;
  out.invuln = invulnTicks > 0;
}

/** Sim-state fields of `out` from a world bean. Identity, `isLocal` and `crowned` are untouched. */
export function beanViewFromBean(out: BeanView, b: Bean): void {
  out.active = b.active;
  out.state = b.state;
  out.st = b.st;
  out.x = b.pos.x;
  out.y = b.pos.y;
  out.z = b.z;
  out.vx = b.vel.x;
  out.vy = b.vel.y;
  out.vz = b.vz;
  out.facing = b.facing;
  out.radius = b.radius;
  out.dizzy = b.dizzy;
  out.item = b.item;
  out.charges = b.charges;
  out.out = b.out;
  out.hasPotato = b.hasPotato;
  out.holdsCrown = b.holdsCrown;
  out.partner = b.partner;
  out.edgeX = b.edgePoint.x;
  out.edgeY = b.edgePoint.y;
  out.edgeNx = b.edgeNormal.x;
  out.edgeNy = b.edgeNormal.y;
  out.place = b.place;
  out.crownTicks = b.crownTicks;
  out.readyTicks = b.readyTicks;
  out.ledgeCd = b.cdLedge;
  setDerivedFlags(out, b.shakeTicks, b.floatTicks, b.invulnTicks);
}

/** Sim-state fields of an active bean from a decoded snapshot record, without interpolation. */
export function beanViewFromSnap(out: BeanView, s: SnapBean): void {
  out.active = true;
  out.state = s.state;
  out.st = s.st;
  out.x = s.x;
  out.y = s.y;
  out.z = s.z;
  out.vx = s.vx;
  out.vy = s.vy;
  out.vz = s.vz;
  out.facing = s.facing;
  out.radius = radiusFor(s.shakeTicks);
  out.dizzy = s.dizzy;
  out.item = s.item;
  out.charges = s.charges;
  out.out = (s.flags & BeanFlag.OUT) !== 0;
  out.hasPotato = (s.flags & BeanFlag.HAS_POTATO) !== 0;
  out.holdsCrown = (s.flags & BeanFlag.HOLDS_CROWN) !== 0;
  out.partner = s.partner;
  out.edgeX = s.edgeX;
  out.edgeY = s.edgeY;
  out.edgeNx = Math.cos(s.edgeN);
  out.edgeNy = Math.sin(s.edgeN);
  out.place = s.place;
  out.crownTicks = s.crownTicks;
  out.readyTicks = s.readyTicks;
  out.ledgeCd = s.cdLedge;
  setDerivedFlags(out, s.shakeTicks, s.floatTicks, s.invulnTicks);
}

export function createEntityView(): EntityView {
  return { id: 0, kind: EK.CRATE, owner: NO_SLOT, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, t: 0, timer: 0, data: 0, data2: 0, pending: false };
}

export function entityViewFromEntity(out: EntityView, e: Entity): void {
  out.id = e.id;
  out.kind = e.kind;
  out.owner = e.owner;
  out.x = e.pos.x;
  out.y = e.pos.y;
  out.z = e.z;
  out.vx = e.vel.x;
  out.vy = e.vel.y;
  out.vz = e.vz;
  out.t = e.t;
  out.timer = e.timer;
  out.data = e.data;
  out.data2 = e.data2;
  out.pending = false;
}

/** All fields of a server entity from a decoded snapshot record, without interpolation. */
export function entityViewFromSnap(out: EntityView, e: SnapEnt): void {
  out.id = e.id;
  out.kind = e.kind;
  out.owner = e.owner;
  out.x = e.x;
  out.y = e.y;
  out.z = e.z;
  out.vx = e.vx;
  out.vy = e.vy;
  out.vz = e.vz;
  out.t = e.t;
  out.timer = e.timer;
  out.data = e.data;
  out.data2 = e.data2;
  out.pending = false;
}

/** Grows or truncates `list` to `n` entries, reusing the existing view objects. */
export function resizeEntityViews(list: EntityView[], n: number): void {
  while (list.length < n) list.push(createEntityView());
  list.length = n;
}

/** Copies every MapState field; the fixed-length arrays are copied in place. */
export function copyMapState(dst: MapState, src: MapState): void {
  dst.tiltX = src.tiltX;
  dst.tiltY = src.tiltY;
  dst.wedgeFallen = src.wedgeFallen;
  dst.wedgeWarn = src.wedgeWarn;
  for (let i = 0; i < src.wedgeOrder.length; i++) dst.wedgeOrder[i] = src.wedgeOrder[i];
  for (let i = 0; i < src.tileState.length; i++) dst.tileState[i] = src.tileState[i];
  for (let i = 0; i < src.tileTimer.length; i++) dst.tileTimer[i] = src.tileTimer[i];
  dst.sdCursor = src.sdCursor;
  dst.cupOccupant = src.cupOccupant;
}

/** Replicated mode fields; `ModeState` and the snapshot's `SnapMode` (matchPoint 0/1) both satisfy it. */
export interface ModeSource { potatoSlot: number; potatoIntensity: number; crownHolder: number; matchPoint: boolean | number }

/** Fills `view.mode` from mode state plus the view's clock, round tick and beans. */
export function fillModeView(view: FrameView, m: ModeSource): void {
  const mode = view.mode;
  mode.potatoSlot = m.potatoSlot;
  mode.potatoIntensity = m.potatoIntensity;
  mode.crownHolder = m.crownHolder;
  mode.crownGoalTicks = TUNE.crown.goalTicks;
  mode.matchPoint = m.matchPoint === true || m.matchPoint === 1;
  mode.timeLeftSec = Math.max(0, (view.clock.cap - Math.max(0, view.roundTick)) / TICK_HZ);
  let alive = 0;
  for (const b of view.beans) if (b.active && !b.out && b.state !== BS.FALLING) alive++;
  mode.alive = alive;
}

/** Round-level fields of `view` from the round's config. */
export function setRoundInfo(view: FrameView, cfg: RoundConfig, roundId: number): void {
  view.mapId = cfg.mapId;
  view.modeId = cfg.modeId;
  view.golden = cfg.golden;
  view.mutator = cfg.mutator;
  view.roundId = roundId;
}

/** Post grade (§12.8): GAME! after the match's final KO, ghost while the local bean is out, gold in the Golden Round. */
export function frameGrade(view: FrameView, gameOver: boolean): FrameView['grade'] {
  if (gameOver) return 'game';
  const local = view.localSlot === NO_SLOT ? null : view.beans[view.localSlot];
  if (local && local.active && local.out) return 'ghost';
  return view.golden ? 'gold' : 'normal';
}

/** Context for `fillFrameFromWorld` that the world itself does not carry. */
export interface WorldFrameInfo {
  now: number; dt: number; source: FrameView['source'];
  roomPhase: RoomPhase; roundIndex: number; totalRounds: number;
  localSlot: number; focusSlot: number;
  /** Fractional round tick to present (clock, timer and hazard poses). */
  tick: number;
  players: readonly (BeanIdentity | null)[] | null;
  /** The round map's module, for hazard poses; null leaves `view.hazard` untouched. */
  map: MapDef | null;
}

/**
 * A complete FrameView straight from a World (offline sources such as LocalSession, and the online
 * view before the first snapshot). `excitement` and `replay` are left untouched.
 */
export function fillFrameFromWorld(view: FrameView, w: World, info: WorldFrameInfo): FrameView {
  view.now = info.now;
  view.dt = info.dt;
  view.timeScale = 1;
  view.source = info.source;
  view.roomPhase = info.roomPhase;
  view.roundIndex = info.roundIndex;
  view.totalRounds = info.totalRounds;
  setRoundInfo(view, w.cfg, w.roundId);
  view.renderTick = info.tick;
  view.localTick = info.tick;
  view.roundTick = info.tick;
  roundClock(w.cfg.mapId, w.cfg.modeId, info.tick, w.cfg.capTickOverride, view.clock);
  view.localSlot = info.localSlot;
  view.focusSlot = info.focusSlot;
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    const bv = view.beans[slot];
    beanViewFromBean(bv, w.beans[slot]);
    setBeanIdentity(bv, info.players?.[slot] ?? null);
    bv.isLocal = slot === info.localSlot;
    bv.crowned = slot === w.cfg.crownedSlot;
  }
  resizeEntityViews(view.ents, w.ents.length);
  for (let i = 0; i < w.ents.length; i++) entityViewFromEntity(view.ents[i], w.ents[i]);
  copyMapState(view.map, w.map);
  info.map?.hazardPose(w, info.tick, view.hazard);
  fillModeView(view, w.mode);
  view.grade = frameGrade(view, false);
  return view;
}
